import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Dialog, Transition } from '@headlessui/react';
import { CUSTOM_EVENTS, CUSTOM_EVENT_CATEGORIES } from '../../../variables';
import { updateElementData, setBuilderMode, setSelected, setWebhookId, setElements, setBuilderIndex } from '../../../actions';
import server from '../../../api/server';
import buildExistingCommand from '../commandbuilder/buildExistingCommand';
import LZString from 'lz-string';
import { layoutElements } from '../commandbuilder/eventUtils';
import ReactGA from 'react-ga';

class CustomEventSelector extends Component {
    constructor(props) {
        super(props);
        this.state = {
            searchTerm: '',
            selectedCategory: 'all',
            importCode: '',
            importError: '',
            isImporting: false,
            showImportSection: false
        };
    }

    updateData = (key, value) => {
        const { data, updateElementData } = this.props;
        const updatedData = { ...data };
        updatedData[key] = value;
        updateElementData({
            data: updatedData,
            id: 'root'
        });
    };

    handleEventChange = (eventValue) => {
        const { data, updateElementData, setBuilderMode, setSelected } = this.props;
        const value = eventValue;
        console.log(value);

        if (value.startsWith("WEBHOOK_")) {
            const updatedData = { ...data };
            updatedData.type = "webhookExecuted";
            updatedData.webhookId = value.replace("WEBHOOK_", "");
            updateElementData({
                data: updatedData,
                id: 'root'
            });

            setBuilderMode({
                mode: "event",
                components: {
                    Guild: true,
                    Channel: false,
                    User: false,
                    Member: false,
                    Message: false,
                    Webhook: true,
                    variables: []
                },
            });
            setSelected('root');
            return;
        }

        if (value.startsWith("TIMED_EVENT_")) {
            const updatedData = { ...data };
            updatedData.type = "timedEvent";
            updatedData.timer_id = value.replace("TIMED_EVENT_", "");
            updatedData.guild_id = "{DEFAULT_SERVER}";
            updateElementData({
                data: updatedData,
                id: 'root'
            });

            setBuilderMode({
                mode: "event",
                components: {
                    Guild: true,
                    Channel: false,
                    User: false,
                    Member: false,
                    Message: false,
                    variables: []
                },
            });
            setSelected('root');
            return;
        }

        this.updateData('type', value);
        const event_options = CUSTOM_EVENTS.find(event => event.value === value);

        setBuilderMode({
            mode: "event",
            components: event_options.components
        });
        setSelected('root');
    };

    handleSearchChange = (e) => {
        this.setState({
            searchTerm: e.target.value,
            selectedCategory: 'all' // Reset category selection when searching
        });
    };

    handleCategoryChange = (category) => {
        this.setState(prevState => ({
            selectedCategory: prevState.selectedCategory === category ? 'all' : category
        }));
    };

    handleImportCodeChange = (e) => {
        this.setState({ importCode: e.target.value.trim(), importError: '' });
    };

    toggleImportSection = () => {
        this.setState(prevState => ({
            showImportSection: !prevState.showImportSection,
            importCode: '',
            importError: ''
        }));
    };

    handleImport = async () => {
        const { importCode } = this.state;
        if (!importCode) return;

        this.setState({ isImporting: true, importError: '' });

        try {
            const response = await server.post("/dashboard/importcommand", { id: importCode });

            if (response.data === false || response.data.command === undefined) {
                this.setState({
                    importError: "Could not find an event with that share code.",
                    isImporting: false
                });
            } else {
                if (response.data.type && response.data.type !== "event") {
                    this.setState({
                        importError: "This is not an event share code.",
                        isImporting: false
                    });
                } else {
                    // Replicate CommandBuilderNav's importCommand logic
                    const command = response.data.command;
                    const type = response.data.type;

                    let elements = [];
                    let errorElements = [];

                    if ("compressed_tree" in command && command.compressed_tree != null) {
                        const compressedString = LZString.decompressFromBase64(command.compressed_tree);
                        const tree = JSON.parse(compressedString);
                        elements = tree;
                    } else if ("compressed_actions" in command && command.compressed_actions) {
                        const compressedString = LZString.decompressFromBase64(command.compressed_actions);
                        const actions = JSON.parse(compressedString);
                        command.actions = actions;

                        if ("errors" in command) {
                            errorElements = await buildExistingCommand(
                                {
                                    options: [],
                                    actions: command.errors
                                },
                                type,
                                true
                            );
                        } else {
                            errorElements = await buildExistingCommand(
                                {
                                    options: [],
                                    actions: [
                                        {
                                            nodeID: "action_3_6d1a_9853",
                                            embed: {
                                                color: "#FF0000",
                                                description: "{error_reason}",
                                                title: ":x: {error_message}",
                                                footer: "{error_block}"
                                            },
                                            emojis: [],
                                            target: { reply: true },
                                            type: "embed"
                                        }
                                    ]
                                },
                                type,
                                true
                            );
                        }

                        elements = await buildExistingCommand(command, type);
                        elements = elements.concat(errorElements);
                    } else {
                        elements = await buildExistingCommand(command, type);
                    }

                    // Handle custom variables if present
                    if ("custom_variables" in command && this.props.bot && this.props.bot.commands) {
                        const custom_variables = command.custom_variables;
                        const existing_variables = { ...this.props.bot.commands.variables };

                        custom_variables.forEach((variable) => {
                            const variable_settings = existing_variables.variables.find((v) => v.reference === variable.reference);
                            if (!variable_settings && variable) {
                                existing_variables.variables.push(variable);
                            }
                        });

                        this.props.setBotModule({
                            module: "variables",
                            module_data: existing_variables
                        });
                    }

                    // Set the correct builder mode
                    if (type === "event") {
                        const event_options = CUSTOM_EVENTS.find((event) => event.value === command.type);
                        if (event_options) {
                            this.props.setBuilderMode({
                                mode: "event",
                                components: event_options.components
                            });
                        }
                    }

                    // Update the elements with layout
                    this.props.setElements(elements);
                    const blocks = [...elements];
                    const layout = layoutElements(blocks);
                    this.props.setElements(layout);
                    this.props.setSelected(null);
                    this.props.setSelected("root");

                    // Log the import
                    ReactGA.initialize("UA-122665050-1");
                    ReactGA.event({
                        category: "Import Command",
                        label: "Event Imported",
                        action: "New Command Builder"
                    });

                    this.props.closeModal();
                }
            }
        } catch (error) {
            console.error("Import error:", error);
            this.setState({
                importError: "An error occurred while importing the event.",
                isImporting: false
            });
        }
    };

    renderEventsByCategory = () => {
        const { beta, webhooks, timed_events, data } = this.props;
        const { searchTerm, selectedCategory } = this.state;

        // Prepare all events
        let allEvents = [];

        // Get regular events
        const regularEvents = CUSTOM_EVENTS
            .filter(event =>
                !event.hidden &&
                (!event.beta || beta) &&
                event.label.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map(event => ({
                id: event.value,
                label: event.label,
                category: event.event_category,
                categoryLabel: CUSTOM_EVENT_CATEGORIES.find(cat => cat.value === event.event_category)?.label || "Other",
                icon: event.icon || "🔔"
            }));

        allEvents.push(...regularEvents);

        // Add webhook events
        if (webhooks?.webhooks?.length > 0) {
            const webhookEvents = webhooks.webhooks
                .filter(webhook => webhook.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(webhook => ({
                    id: `WEBHOOK_${webhook.id}`,
                    label: `${webhook.name} Webhook`,
                    category: 'webhooks',
                    categoryLabel: "Webhooks",
                    icon: "🔗"
                }));

            allEvents.push(...webhookEvents);
        }

        // Add timed events
        if (timed_events?.events?.length > 0) {
            const timedEvents = timed_events.events
                .filter(event => event.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(event => ({
                    id: `TIMED_EVENT_${event.id}`,
                    label: `${event.name} Timed Event`,
                    category: 'timed_events',
                    categoryLabel: "Timed Events",
                    icon: "⏱️"
                }));

            allEvents.push(...timedEvents);
        }

        // Filter by selected category
        if (selectedCategory !== 'all') {
            allEvents = allEvents.filter(event => event.category === selectedCategory);
        }

        // Group events by category
        const eventsByCategory = {};

        allEvents.forEach(event => {
            if (!eventsByCategory[event.categoryLabel]) {
                eventsByCategory[event.categoryLabel] = [];
            }
            eventsByCategory[event.categoryLabel].push(event);
        });

        // Sort categories by name
        const sortedCategories = Object.keys(eventsByCategory).sort();

        if (sortedCategories.length === 0) {
            return (
                <div className="text-center py-6">
                    <p className="text-[#9CA3AF] text-sm">No events found matching your criteria</p>
                </div>
            );
        }

        return (
            <div className="max-h-[400px] overflow-y-auto pr-2">
                {sortedCategories.map(categoryLabel => (
                    <div key={categoryLabel} className="mb-4">
                        <div className="text-[#9CA3AF] text-xs font-medium uppercase tracking-wider mb-1.5 border-b border-[#2A2A2A] pb-1">
                            {categoryLabel}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {eventsByCategory[categoryLabel].map(event => (
                                <div
                                    key={event.id}
                                    onClick={() => this.handleEventChange(event.id)}
                                    className={`p-2 rounded-md border cursor-pointer transition-all ${data.type === event.id
                                        ? 'bg-[#1F2937] border-[#5865f2]'
                                        : 'bg-[#1C1C1C] border-[#2A2A2A] hover:bg-[#1F2937]'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="text-xl text-[#F59E0B]">
                                            {event.icon}
                                        </div>
                                        <div className="">
                                            <h4 className="text-[#FFFFFF] text-sm font-medium">{event.label}</h4>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    renderCategoryPills = () => {
        const { selectedCategory } = this.state;
        const { webhooks, timed_events, beta } = this.props;

        // Filter categories to only show ones with available events
        const availableCategories = [];
        const eventCounts = {};

        // Count events by category
        CUSTOM_EVENTS.forEach(event => {
            if ((event.beta && !beta) || event.hidden) return;
            if (!eventCounts[event.event_category]) {
                eventCounts[event.event_category] = 0;
            }
            eventCounts[event.event_category]++;
        });

        // Add categories with events
        availableCategories.push({ id: 'all', label: 'All' });

        CUSTOM_EVENT_CATEGORIES.forEach(cat => {
            if (eventCounts[cat.value] && eventCounts[cat.value] > 0) {
                availableCategories.push({ id: cat.value, label: cat.label });
            }
        });

        // Add webhooks and timed events if available
        if (webhooks?.webhooks?.length > 0) {
            availableCategories.push({ id: 'webhooks', label: 'Webhooks' });
        }

        if (timed_events?.events?.length > 0) {
            availableCategories.push({ id: 'timed_events', label: 'Timed Events' });
        }

        return (
            <div className="flex flex-wrap gap-2 mb-4">
                {availableCategories.map((category) => (
                    <button
                        key={category.id}
                        onClick={() => this.handleCategoryChange(category.id)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedCategory === category.id
                            ? 'bg-[#5865f2] text-[#FFFFFF]'
                            : 'bg-[#2A2A2A] text-[#9CA3AF] hover:bg-[#3A3A3A]'
                            }`}
                    >
                        {category.label}
                    </button>
                ))}
            </div>
        );
    };

    render() {
        const { open, closeModal } = this.props;
        const { showImportSection, importCode, importError, isImporting } = this.state;

        return (
            <Transition.Root show={open} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-[10000]"
                    onClose={closeModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-[#0000008a] transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-[#141414] text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                                    <div className="p-4">
                                        <div className="flex flex-col">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-xl font-bold text-[#FFFFFF] mb-2"
                                            >
                                                Select a Custom Event
                                            </Dialog.Title>

                                            <div className="flex justify-between items-center mb-4">
                                                <p className="text-[#9CA3AF] text-sm">
                                                    Choose from available events or import an event using a share code
                                                </p>
                                                <button
                                                    onClick={this.toggleImportSection}
                                                    className="px-3 py-1.5 bg-[#2A2A2A] hover:bg-[#3A3A3A] text-[#FFFFFF] rounded-md text-sm font-medium transition-all flex items-center"
                                                >
                                                    {showImportSection ? 'Cancel Import' : 'Import Event'}
                                                    {!showImportSection && (
                                                        <svg className="ml-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4"></path>
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>

                                            {showImportSection ? (
                                                <div className="mb-4 p-4 bg-[#1C1C1C] border border-[#2A2A2A] rounded-md">
                                                    <h4 className="text-[#FFFFFF] text-sm font-medium mb-2">Import Event</h4>
                                                    <p className="text-[#9CA3AF] text-sm mb-3">
                                                        Import an event using a share code.
                                                    </p>

                                                    {importError && (
                                                        <div className="mb-3 px-3 py-2 bg-[#EF4444]/10 text-[#EF4444] rounded-md text-sm">
                                                            {importError}
                                                        </div>
                                                    )}

                                                    <div className="mb-3">
                                                        <input
                                                            type="text"
                                                            value={importCode}
                                                            onChange={this.handleImportCodeChange}
                                                            placeholder="EVENT_XXXXXX-XXXXXX-XXXXXX-XXXX"
                                                            className="w-full px-3 py-2 bg-[#2A2A2A] border border-[#374151] rounded-md text-[#FFFFFF] placeholder-[#6B7280] focus:outline-none focus:ring-1 focus:ring-[#5865f2] focus:border-transparent transition-colors"
                                                        />
                                                    </div>

                                                    <div className="flex justify-end">
                                                        <button
                                                            onClick={this.handleImport}
                                                            disabled={!importCode || isImporting}
                                                            className={`px-4 py-2 ${!importCode || isImporting ? 'bg-[#5865f2]/50 cursor-not-allowed' : 'bg-[#5865f2] hover:bg-[#4754e6] shadow-lg shadow-[#5865f2]/20'} text-[#FFFFFF] rounded-md transition-colors font-medium`}
                                                        >
                                                            {isImporting ? 'Importing...' : 'Import'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="mb-4 relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <svg className="h-5 w-5 text-[#6B7280]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                        <input
                                                            type="text"
                                                            placeholder="Search events..."
                                                            value={this.state.searchTerm}
                                                            onChange={this.handleSearchChange}
                                                            className="w-full pl-10 pr-3 py-2 bg-[#1C1C1C] border border-[#374151] rounded-md text-[#FFFFFF] text-sm focus:outline-none focus:ring-1 focus:ring-[#5865f2] focus:border-transparent transition-colors"
                                                        />
                                                    </div>

                                                    {this.renderCategoryPills()}
                                                    {this.renderEventsByCategory()}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        );
    }
}

const mapStateToProps = (state) => ({
    beta: state.data.beta,
    webhooks: state.data.bot.commands.webhooks,
    timed_events: state.data.bot.commands.timed_events,
    user: state.data.user,
    bot: state.data.bot,
    elements: state.builder.elements
});

const mapDispatchToProps = {
    updateElementData,
    setBuilderMode,
    setSelected,
    setWebhookId,
    setElements,
    setBuilderIndex,
    setBotModule: (moduleData) => (dispatch) => {
        dispatch({
            type: 'SET_BOT_MODULE',
            payload: moduleData
        });
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomEventSelector);